import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { 
    AlertTriangle, 
    X, 
    ChevronRight, 
    FileText,
    Mail,
    Calendar,
    CheckCircle,
    Clock,
    AlertCircle,
    Ban
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const API_URL = '';

/**
 * Modal de dénonciation de mandat avec triple validation
 * 
 * Étapes :
 * 1. Confirmation initiale ("Êtes-vous sûr ?")
 * 2. Information sur les conséquences et délais
 * 3. Confirmation finale avec envoi du recommandé électronique
 */
function MandateTerminationModal({ isOpen, onClose, mandate, userId, onTerminationComplete }) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [eligibility, setEligibility] = useState(null);
    const [terminationData, setTerminationData] = useState(null);
    const [reason, setReason] = useState('');
    const [confirmationCode, setConfirmationCode] = useState('');

    // Vérifier l'éligibilité à l'ouverture
    React.useEffect(() => {
        if (isOpen && mandate) {
            checkEligibility();
        }
    }, [isOpen, mandate]);

    const checkEligibility = async () => {
        try {
            setLoading(true);
            const response = await api.get(
                `/mandate-termination/eligibility/${mandate.id}`
            );
            setEligibility(response.data.eligibility);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const initiateTermination = async () => {
        try {
            setLoading(true);
            const response = await api.post(
                `/mandate-termination/initiate/${mandate.id}?reason=${encodeURIComponent(reason)}`
            );
            const data = response.data;
            setTerminationData(data);
            setConfirmationCode(data.confirmation_code);
            setStep(3);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'initiation');
        } finally {
            setLoading(false);
        }
    };

    const confirmTermination = async () => {
        try {
            setLoading(true);
            const response = await api.post('/mandate-termination/confirm', {
                mandate_id: mandate.id,
                reason: reason,
                confirmation_code: confirmationCode
            });
            toast.success('Dénonciation enregistrée avec succès');
            onTerminationComplete?.(response.data);
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la confirmation');
        } finally {
            setLoading(false);
        }
    };

    const resetAndClose = () => {
        setStep(1);
        setEligibility(null);
        setTerminationData(null);
        setReason('');
        setConfirmationCode('');
        onClose();
    };

    if (!isOpen) return null;

    // Vérifier si la dénonciation est possible
    const canTerminate = eligibility?.can_terminate;
    const terminationType = eligibility?.termination_type;
    const isRetractation = terminationType === 'retractation';

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={resetAndClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-card border border-border rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-border">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                canTerminate ? 'bg-amber-500/20' : 'bg-red-500/20'
                            }`}>
                                {canTerminate ? (
                                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                                ) : (
                                    <Ban className="w-5 h-5 text-red-400" />
                                )}
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">
                                    {isRetractation ? 'Rétractation du mandat' : 'Dénonciation du mandat'}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    {mandate?.mandate_number}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={resetAndClose}
                            className="p-2 hover:bg-muted rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-muted-foreground" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-2 border-white/20 border-t-white"></div>
                            </div>
                        ) : !canTerminate ? (
                            // Non éligible
                            <div className="space-y-6">
                                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                                        <div>
                                            <p className="font-medium text-red-300">
                                                Dénonciation impossible
                                            </p>
                                            <p className="text-sm text-red-300/70 mt-1">
                                                {eligibility?.reason}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                {eligibility?.details?.days_until_can_terminate && (
                                    <div className="bg-card rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <Clock className="w-5 h-5 text-blue-400" />
                                            <div>
                                                <p className="text-sm text-muted-foreground">Vous pourrez dénoncer dans</p>
                                                <p className="text-2xl font-bold text-white">
                                                    {eligibility.details.days_until_can_terminate} jours
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                <Button
                                    onClick={resetAndClose}
                                    className="w-full bg-muted hover:bg-white/20 text-white"
                                >
                                    J'ai compris
                                </Button>
                            </div>
                        ) : step === 1 ? (
                            // Étape 1 : Confirmation initiale
                            <div className="space-y-6">
                                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                                    <p className="font-medium text-amber-300">
                                        {isRetractation 
                                            ? "Vous êtes dans le délai légal de rétractation de 14 jours."
                                            : "Vous allez dénoncer votre mandat de vente."
                                        }
                                    </p>
                                    <p className="text-sm text-amber-300/70 mt-2">
                                        Cette action est <strong>irréversible</strong>.
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <p className="text-sm font-medium text-muted-foreground">
                                        Pourquoi souhaitez-vous {isRetractation ? 'vous rétracter' : 'dénoncer ce mandat'} ? (optionnel)
                                    </p>
                                    <textarea
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder="Ex: J'ai trouvé un acheteur par moi-même, je change d'avis sur la vente..."
                                        className="w-full h-24 px-4 py-3 bg-card border border-border rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-white/30 resize-none"
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={resetAndClose}
                                        className="flex-1 border-border text-muted-foreground hover:bg-card"
                                    >
                                        Annuler
                                    </Button>
                                    <Button
                                        onClick={() => setStep(2)}
                                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                                    >
                                        Continuer
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        ) : step === 2 ? (
                            // Étape 2 : Informations sur les conséquences
                            <div className="space-y-6">
                                <div className="bg-card rounded-xl p-4 space-y-4">
                                    <h3 className="font-semibold text-white flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-[#FF914D]" />
                                        Ce qui va se passer
                                    </h3>
                                    
                                    <div className="space-y-3">
                                        <div className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-xs font-bold text-blue-400">1</span>
                                            </div>
                                            <div>
                                                <p className="text-white font-medium">Envoi d'un recommandé électronique</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Une lettre recommandée électronique sera envoyée à l'agence avec accusé de réception.
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {!isRetractation && (
                                            <div className="flex items-start gap-3">
                                                <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                    <span className="text-xs font-bold text-amber-400">2</span>
                                                </div>
                                                <div>
                                                    <p className="text-white font-medium">Préavis de 15 jours</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        L'agence pourra continuer à commercialiser votre bien pendant 15 jours.
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="flex items-start gap-3">
                                            <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-xs font-bold text-green-400">{isRetractation ? '2' : '3'}</span>
                                            </div>
                                            <div>
                                                <p className="text-white font-medium">Fin du mandat</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {isRetractation 
                                                        ? "La rétractation prend effet immédiatement."
                                                        : "Le mandat sera définitivement terminé à la fin du préavis."
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {!isRetractation && (
                                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <Calendar className="w-5 h-5 text-blue-400" />
                                            <div>
                                                <p className="text-sm text-blue-300/70">Date de fin effective</p>
                                                <p className="font-semibold text-blue-300">
                                                    {new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR', {
                                                        weekday: 'long',
                                                        day: 'numeric',
                                                        month: 'long',
                                                        year: 'numeric'
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={() => setStep(1)}
                                        className="flex-1 border-border text-muted-foreground hover:bg-card"
                                    >
                                        Retour
                                    </Button>
                                    <Button
                                        onClick={initiateTermination}
                                        disabled={loading}
                                        className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                                    >
                                        {loading ? 'Chargement...' : 'Confirmer'}
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        ) : step === 3 ? (
                            // Étape 3 : Confirmation finale
                            <div className="space-y-6">
                                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                                        <div>
                                            <p className="font-bold text-red-300">
                                                Dernière étape - Action irréversible
                                            </p>
                                            <p className="text-sm text-red-300/70 mt-1">
                                                En confirmant, vous envoyez officiellement votre {isRetractation ? 'rétractation' : 'dénonciation'} à l'agence.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-card rounded-xl p-4 space-y-3">
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-5 h-5 text-[#FF914D]" />
                                        <p className="text-white font-medium">
                                            Un recommandé électronique sera envoyé
                                        </p>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Ce document aura valeur légale et servira de preuve de votre {isRetractation ? 'rétractation' : 'dénonciation'}.
                                    </p>
                                </div>

                                {terminationData?.warnings && (
                                    <ul className="space-y-2">
                                        {terminationData.warnings.map((warning, index) => (
                                            <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                <CheckCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                                {warning}
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <div className="flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={() => setStep(2)}
                                        className="flex-1 border-border text-muted-foreground hover:bg-card"
                                    >
                                        Retour
                                    </Button>
                                    <Button
                                        onClick={confirmTermination}
                                        disabled={loading}
                                        className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                                    >
                                        {loading ? 'Envoi en cours...' : isRetractation ? 'Confirmer la rétractation' : 'Confirmer la dénonciation'}
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </div>

                    {/* Progress indicator */}
                    {canTerminate && (
                        <div className="px-6 pb-6">
                            <div className="flex items-center gap-2">
                                {[1, 2, 3].map((s) => (
                                    <div
                                        key={s}
                                        className={`h-1 flex-1 rounded-full transition-colors ${
                                            s <= step ? 'bg-[#FF914D]' : 'bg-muted'
                                        }`}
                                    />
                                ))}
                            </div>
                            <p className="text-xs text-muted-foreground text-center mt-2">
                                Étape {step} sur 3
                            </p>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

export default MandateTerminationModal;
